import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner"
;

interface Department {
  id: string;
  name: string;
}

interface ChangeDepartmentProps {
  employee: {
    id: string
    name: string
    department: string
  }
  onSave: (id: string, newDepartment: string, reason: string) => void
  onCancel: () => void
}

export function ChangeDepartment({ employee, onSave, onCancel }: ChangeDepartmentProps) {
  const [newDepartment, setNewDepartment] = useState<string>(employee.department)
  const [reason, setReason] = useState("")
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      setIsLoadingDepartments(true);
      try {
        const { data, error } = await supabase
          .from('departments')
          .select('id, name')
          .order('name');
          
        if (error) throw error;
        setDepartments(data || []);
      } catch {
        toast.error('Failed to load departments');
      } finally {
        setIsLoadingDepartments(false);
      }
    };
    fetchDepartments();
  }, []);
  
  const handleSubmit = () => {
    if (newDepartment === employee.department) {
      toast.info("Please select a different department.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please provide a reason for the change.");
      return;
    }
    
    onSave(employee.id, newDepartment, reason)
  }
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold">Change Department</h2>
        <p className="text-muted-foreground">Move {employee.name} to a different department</p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Current Department</Label>
          <div className="p-2 border rounded-md bg-muted">
            {employee.department}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="new-department">New Department</Label>
          <div className="flex flex-col gap-2">
            {departments.map(dept => (
              <label key={dept.id} className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={newDepartment === dept.name}
                  onChange={() => setNewDepartment(dept.name)}
                  disabled={isLoadingDepartments}
                />
                <span>{dept.name}</span>
              </label>
            ))}
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="reason">Reason for Change *</Label>
          <Textarea 
            id="reason"
            placeholder="Provide a reason for this department change"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="secondary"
          disabled={newDepartment === employee.department || !reason.trim()}
        >
          Change Department
        </Button>
      </div>
    </div>
  )
}
